import React, { useEffect, useState } from "react";
import { api, formatIDR, formatDate } from "@/lib/api";
import { toast } from "sonner";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { KeyRound, AlertTriangle } from "lucide-react";
import { useAuth } from "@/contexts/AuthContext";

export default function Sales() {
  const { hasFeature } = useAuth();
  const canRefund = hasFeature("refunds");
  const [rows, setRows] = useState([]);
  const [outlets, setOutlets] = useState([]);
  const [filter, setFilter] = useState("");
  const [refundTarget, setRefundTarget] = useState(null);
  const [pin, setPin] = useState("");
  const [reason, setReason] = useState("");
  const [submitting, setSubmitting] = useState(false);

  const load = async () => {
    const [s, o] = await Promise.all([api.get("/sales", { params: { limit: 500 } }), api.get("/outlets")]);
    setRows(s.data || []); setOutlets(o.data || []);
  };
  useEffect(() => { load(); }, []);

  const openRefund = (sale) => { if (!canRefund) return; setRefundTarget(sale); setPin(""); setReason("Customer cancelation"); };

  const submitRefund = async () => {
    if (!pin || pin.length < 4) { toast.error("PIN supervisor minimal 4 digit"); return; }
    setSubmitting(true);
    try {
      const r = await api.post(`/sales/${refundTarget.id}/refund`, { supervisor_pin: pin, reason });
      toast.success(`Refund disetujui oleh ${r.data?.approver || "supervisor"}`);
      setRefundTarget(null);
      load();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Refund gagal");
    } finally { setSubmitting(false); }
  };

  const filtered = rows.filter((r) => !filter || r.invoice_no?.includes(filter) || r.cashier_name?.toLowerCase().includes(filter.toLowerCase()));
  const outletMap = Object.fromEntries(outlets.map(o => [o.id, o]));

  return (
    <div className="p-6 lg:p-10">
      <div className="mb-6">
        <div className="overline text-zinc-500">RIWAYAT</div>
        <h1 className="font-display font-black text-4xl tracking-tighter">Penjualan</h1>
      </div>
      <input data-testid="sales-filter-input" value={filter} onChange={(e)=>setFilter(e.target.value)} placeholder="Cari nomor invoice / kasir…" className="mb-4 w-full md:w-80 border border-border px-4 py-2.5 text-sm"/>
      <div className="border border-border overflow-x-auto">
        <table className="w-full text-sm">
          <thead className="bg-zinc-50 border-b border-border"><tr>
            <th className="text-left p-3 overline">Waktu</th>
            <th className="text-left p-3 overline">No. Invoice</th>
            <th className="text-left p-3 overline">Outlet</th>
            <th className="text-left p-3 overline">Kasir</th>
            <th className="text-left p-3 overline">Metode</th>
            <th className="text-left p-3 overline">Status</th>
            <th className="text-right p-3 overline">Total</th>
            <th className="text-right p-3 overline">Aksi</th>
          </tr></thead>
          <tbody>
            {filtered.length===0 && <tr><td colSpan={8} className="p-8 text-center text-zinc-500">Belum ada penjualan</td></tr>}
            {filtered.map((r) => (
              <tr key={r.id} className="border-b border-border hover:bg-zinc-50">
                <td className="p-3 text-xs text-zinc-500">{formatDate(r.created_at)}</td>
                <td className="p-3 font-mono text-xs">{r.invoice_no}</td>
                <td className="p-3">{outletMap[r.outlet_id]?.name || "-"}</td>
                <td className="p-3 text-zinc-500">{r.cashier_name}</td>
                <td className="p-3 uppercase text-xs tracking-wider">{r.payment_method}{r.payment_method==="qris" && r.qris?.provider ? ` · ${r.qris.provider}` : ""}</td>
                <td className="p-3"><span className={"text-xs px-2 py-0.5 uppercase tracking-wider font-semibold " + (r.payment_status==="paid"?"bg-emerald-100 text-emerald-800": r.payment_status==="refunded"?"bg-rose-100 text-rose-700":"bg-amber-100 text-amber-800")}>{r.payment_status}</span>{r.refund_approver_name && <div className="text-[10px] text-zinc-400 mt-0.5">Approver: {r.refund_approver_name}</div>}</td>
                <td className="p-3 text-right tabular-nums font-semibold">{formatIDR(r.total)}</td>
                <td className="p-3 text-right">
                  {canRefund && r.payment_status==="paid" && <button onClick={() => openRefund(r)} className="text-xs text-rose-600 underline" data-testid={`refund-${r.id}`}>Refund</button>}
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>

      <Dialog open={canRefund && !!refundTarget} onOpenChange={(o) => !o && setRefundTarget(null)}>
        <DialogContent className="rounded-none border-border max-w-md" data-testid="refund-dialog">
          <DialogHeader>
            <DialogTitle>Refund Transaksi</DialogTitle>
          </DialogHeader>
          {refundTarget && (
            <div className="space-y-4">
              <div className="border border-border p-4 bg-zinc-50">
                <div className="text-xs text-zinc-500 font-mono">{refundTarget.invoice_no}</div>
                <div className="font-display font-black text-2xl tabular-nums mt-1">{formatIDR(refundTarget.total)}</div>
                <div className="text-xs text-zinc-500 mt-1">Metode: {refundTarget.payment_method.toUpperCase()}{refundTarget.qris?.provider ? ` (${refundTarget.qris.provider.toUpperCase()})` : ""}</div>
              </div>
              <div className="flex gap-2 items-start text-xs text-amber-800 bg-amber-50 border border-amber-200 p-3">
                <AlertTriangle className="h-4 w-4 shrink-0 mt-0.5"/>
                <div>Refund memerlukan <strong>PIN persetujuan Supervisor/Admin</strong>. {refundTarget.qris?.provider === "yokke" && <span>Refund Yokke akan memanggil <code>qr-mpm-cancel</code> ke Bank Mandiri.</span>}</div>
              </div>
              <div>
                <label className="overline text-zinc-600 block mb-1.5">PIN Supervisor</label>
                <div className="relative">
                  <KeyRound className="h-4 w-4 absolute left-3 top-1/2 -translate-y-1/2 text-zinc-400"/>
                  <input data-testid="refund-pin-input" type="password" value={pin} onChange={(e)=>setPin(e.target.value)} maxLength={8} autoFocus
                    className="w-full border border-border pl-9 pr-3 py-3 text-center text-2xl font-mono tracking-[0.5em] focus:outline-none focus:ring-2 focus:ring-[#002FA7]" placeholder="••••"/>
                </div>
              </div>
              <div>
                <label className="overline text-zinc-600 block mb-1.5">Alasan</label>
                <input data-testid="refund-reason-input" value={reason} onChange={(e)=>setReason(e.target.value)}
                  className="w-full border border-border px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-[#002FA7]"/>
              </div>
              <div className="grid grid-cols-2 gap-2">
                <button onClick={()=>setRefundTarget(null)} data-testid="refund-cancel-btn" className="py-3 text-sm border border-border">Batal</button>
                <button onClick={submitRefund} disabled={submitting} data-testid="refund-submit-btn" className="py-3 text-sm bg-rose-600 text-white font-semibold hover:bg-rose-700 disabled:opacity-50">{submitting?"Memproses…":"Approve & Refund"}</button>
              </div>
            </div>
          )}
        </DialogContent>
      </Dialog>
    </div>
  );
}
